import React, { Fragment } from 'react';
/**
 * Lightweight markdown renderer with safe defaults.
 * Supports: headings (#, ##, ###), bold (**), italic (*), inline code (`),
 * links [text](url), unordered lists (-, *), ordered lists (1.),
 * blockquotes (>), horizontal rules (---), and line breaks.
 *
 * HTML in the source is treated as plain text (escaped) to prevent XSS.
 */
interface MarkdownProps {
  children: string | null | undefined;
  className?: string;
}
function escapeHtml(s: string) {
  // Used only conceptually — we never inject raw HTML strings. This is just
  // a guard in case we ever do.
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}
// Parse inline markdown: **bold**, *italic*, `code`, [link](url)
function parseInline(text: string, keyPrefix = ''): React.ReactNode[] {
  if (!text) return [];
  const nodes: React.ReactNode[] = [];
  // We tokenize by scanning for the next markdown marker.
  const regex = /(\*\*[^*]+\*\*|\*[^*]+\*|`[^`]+`|\[[^\]]+\]\([^)]+\))/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  let i = 0;
  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      nodes.push(<Fragment key={`${keyPrefix}t${i++}`} data-id="element-281" data-mp="2i39j21uiu7wj">
          {text.slice(lastIndex, match.index)}
        </Fragment>);
    }
    const token = match[0];
    if (token.startsWith('**')) {
      nodes.push(<strong key={`${keyPrefix}b${i++}`} className="font-semibold text-text-primary" data-id="element-282" data-mp="245ba5byyz3of">
          {token.slice(2, -2)}
        </strong>);
    } else if (token.startsWith('*')) {
      nodes.push(<em key={`${keyPrefix}i${i++}`} className="italic" data-id="element-283" data-mp="1ugjmhg0xz5jb">
          {token.slice(1, -1)}
        </em>);
    } else if (token.startsWith('`')) {
      nodes.push(<code key={`${keyPrefix}c${i++}`} className="px-1.5 py-0.5 rounded bg-secondary text-text-primary font-mono text-[0.9em]" data-id="element-284" data-mp="1lek52mm0lzfu">
          {token.slice(1, -1)}
        </code>);
    } else if (token.startsWith('[')) {
      const m = token.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
      if (m) {
        nodes.push(<a key={`${keyPrefix}l${i++}`} href={m[2]} target="_blank" rel="noopener noreferrer" className="text-primary underline hover:text-primary/80" data-id="element-285" data-mp="1av9obj736gql">
            {m[1]}
          </a>);
      } else {
        nodes.push(<span key={`${keyPrefix}x${i++}`} data-id="element-286" data-mp="1pjxd96mffs7k">{token}</span>);
      }
    }
    lastIndex = regex.lastIndex;
  }
  if (lastIndex < text.length) {
    nodes.push(<Fragment key={`${keyPrefix}end`} data-id="element-287" data-mp="1ft2s65kx2ynp">{text.slice(lastIndex)}</Fragment>);
  }
  return nodes;
}
function renderLineWithBreaks(text: string, keyPrefix: string) {
  const lines = text.split('\n');
  return lines.map((line, idx) => <Fragment key={`${keyPrefix}-ln${idx}`} data-id="element-288" data-mp="28gfecen614gh" data-mpi={idx}>
      {parseInline(line, `${keyPrefix}-ln${idx}-`)}
      {idx < lines.length - 1 && <br data-id="element-289" data-mp="2d9hzpzhsh7ll" />}
    </Fragment>);
}
export function Markdown({
  children,
  className = ''
}: MarkdownProps) {
  if (!children) return null;
  // Normalize line endings, strip raw HTML tags defensively.
  const source = String(children).replace(/\r\n/g, '\n').replace(/<[^>]+>/g, m => escapeHtml(m));
  // Split into block-level chunks separated by blank lines
  const blocks = source.split(/\n{2,}/);
  return <div className={`text-[16px] text-text-secondary leading-relaxed space-y-4 ${className}`} data-id="element-290" data-mp="6m13nnr1p5f9">
      {blocks.map((block, bIndex) => {
      const trimmed = block.trim();
      if (!trimmed) return null;
      // Horizontal rule
      if (/^-{3,}$/.test(trimmed) || /^\*{3,}$/.test(trimmed)) {
        return <hr key={bIndex} className="border-border" data-id="element-291" data-mp="3j1l9s2z62qz9" />;
      }
      // Headings
      const headingMatch = trimmed.match(/^(#{1,3})\s+(.*)$/);
      if (headingMatch && !trimmed.includes('\n')) {
        const level = headingMatch[1].length;
        const inner = parseInline(headingMatch[2], `h${bIndex}`);
        if (level === 1) return <h1 key={bIndex} className="text-[24px] font-bold text-text-primary mt-2" data-id="element-292" data-mp="1tndkq5wr0t5d">
                {inner}
              </h1>;
        if (level === 2) return <h2 key={bIndex} className="text-[20px] font-semibold text-text-primary mt-2" data-id="element-293" data-mp="28j2yjgrs5zz0">
                {inner}
              </h2>;
        return <h3 key={bIndex} className="text-[17px] font-semibold text-text-primary mt-2" data-id="element-294" data-mp="2hi6h8jn1cdcz">
              {inner}
            </h3>;
      }
      // Blockquote
      if (trimmed.split('\n').every(l => /^>\s?/.test(l))) {
        const innerText = trimmed.replace(/^>\s?/gm, '');
        return <blockquote key={bIndex} className="border-l-4 border-primary/40 pl-4 italic text-text-secondary" data-id="element-295" data-mp="2w6b5020y3f2m">
              {renderLineWithBreaks(innerText, `q${bIndex}`)}
            </blockquote>;
      }
      // Unordered list
      const lines = trimmed.split('\n');
      if (lines.every(l => /^\s*[-*]\s+/.test(l))) {
        return <ul key={bIndex} className="list-disc pl-5 space-y-1.5" data-id="element-296" data-mp="aml1w2k2jv9b">
              {lines.map((line, li) => <li key={li} data-id="element-297" data-mp="18a3gvv4bptlr" data-mpi={li}>
                  {parseInline(line.replace(/^\s*[-*]\s+/, ''), `ul${bIndex}-${li}-`)}
                </li>)}
            </ul>;
      }
      // Ordered list
      if (lines.every(l => /^\s*\d+\.\s+/.test(l))) {
        return <ol key={bIndex} className="list-decimal pl-5 space-y-1.5" data-id="element-298" data-mp="tl73dijhzodr">
              {lines.map((line, li) => <li key={li} data-id="element-299" data-mp="1r2nxoe6r0897" data-mpi={li}>
                  {parseInline(line.replace(/^\s*\d+\.\s+/, ''), `ol${bIndex}-${li}-`)}
                </li>)}
            </ol>;
      }
      // Regular paragraph
      return <p key={bIndex} data-id="element-300" data-mp="3kisd5hq0cry2" data-mpi={bIndex}>{renderLineWithBreaks(trimmed, `p${bIndex}`)}</p>;
    })}
    </div>;
}